import React, { Component } from "react";
import ReactDOM from "react-dom";
import { noop, keyCodes, isNil } from "lib";
import { getCurrentRangeValues, setRangeForTarget } from "lib/selectionUtils";
import {
  sanitizeHTMLForTextUsingDOMNodes,
  DOM_NODE_TYPES
} from "lib/textUtils";
import {
  getNearestContentEditableParentFromSelection,
  toggleBoldOnSelection,
  indentSelection,
  toggleItalicOnSelection,
  toggleUnderlineOnSelection,
  splitListItem,
  getRange,
  getTextContentBeforeNode
} from "lib/DOMNodeUtils";
import { nestListInPreviousSibling } from "lib/richText/IndentationUtils";
import style from "./style.module.css";
import PATHS from "routes/paths";
import { isRichTextColorChange } from "lib/htmlStrings";
import { saveCurrentRange, applySavedSelection } from "lib/DOMNodeUtils";
import {
  isKerningOnlyChange,
  parseKerningSpans,
  stripKerningOverridesHTML
} from "lib/kerning";

const OBSERVER_CONFIG = {
  attributes: true,
  attributeOldValue: true,
  childList: true,
  subtree: true,
  characterData: true
};

// Walks a contentEditable subtree in document order and returns each
// data-kerning span paired with the plain-text position it wraps. <br>
// counts as one position (mirrors applyKerningOverrides + plainTextFromHTML),
// keeping the indexing the in-place style update uses identical to the one
// the wrap path uses.
const collectKerningSpansWithPositions = root => {
  const out = [];
  let position = 0;
  const walk = node => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        position += child.textContent.length;
        continue;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) continue;
      if (child.nodeName === "BR") {
        position += 1;
        continue;
      }
      if (child.matches('span[data-kerning="1"]')) {
        out.push({ el: child, position });
        position += child.textContent.length;
        continue;
      }
      walk(child);
    }
  };
  walk(root);
  return out;
};

class UncontrolledContentEditable extends Component {
  static defaultProps = {
    onMouseEnter: noop,
    onMouseLeave: noop,
    onClick: noop,
    onDoubleClick: noop,
    onKeyDown: noop
  };

  constructor(props) {
    super(props);

    this.handleChange = this.handleChange.bind(this);
    this.handlePaste = this.handlePaste.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.forceUpdateHTML = this.forceUpdateHTML.bind(this);
    this.replaceEnterWithLineBreak = this.replaceEnterWithLineBreak.bind(this);
    this.handleObserverTrigger = this.handleObserverTrigger.bind(this);

    this.ref = React.createRef();

    this.state = {
      initialText: this.props.text
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.editable && !this.props.editable) {
      this.setState({
        initialText: nextProps.text
      });
    }
  }

  UNSAFE_componentWillUpdate(nextProps) {
    if (!nextProps.editable && this.props.editable) {
      ReactDOM.findDOMNode(this).innerHTML = this.state.initialText;
    }
  }

  replaceEnterWithLineBreak(event) {
    if (event.key === "Enter" && !this.props.isAutoCompleteOpen) {
      // conditionally add new list item when in a li
      const range = getRange();

      const anchorParent = range.startContainer.parentNode;
      let usedExecCommand = false;
      if (
        !event.shiftKey &&
        range.startOffset === range.endOffset &&
        range.startContainer.nodeName === "#text"
      ) {
        const liNode =
          anchorParent.nodeName === "LI"
            ? anchorParent
            : anchorParent.closest && anchorParent.closest("li");

        if (liNode && anchorParent.nodeName === "LI") {
          // Plain text directly in LI — use existing split logic
          splitListItem(range.startContainer, liNode, range.startOffset);
        } else if (liNode && range.startOffset === 0) {
          // Styled text inside LI at position 0 — check if cursor is at the
          // very start of the LI's text content
          const liTextBefore = getTextContentBeforeNode(
            liNode,
            range.startContainer
          );
          if (liTextBefore === "") {
            const emptyLi = document.createElement("li");
            emptyLi.innerHTML = "<br>";
            liNode.parentNode.insertBefore(emptyLi, liNode);
            const newRange = new Range();
            newRange.setStart(range.startContainer, 0);
            newRange.collapse(true);
            document.getSelection().removeAllRanges();
            document.getSelection().addRange(newRange);
          } else {
            document.execCommand("insertLineBreak");
            usedExecCommand = true;
          }
        } else {
          // TODO: For non-zero cursor positions in styled LI content, we fall
          // through to insertLineBreak which inserts a <br> within the LI
          // rather than splitting into two list items. Splitting styled content
          // at arbitrary positions would require refactoring splitListItem to
          // handle sibling nodes at the span level.
          document.execCommand("insertLineBreak");
          usedExecCommand = true;
        }
      } else {
        document.execCommand("insertLineBreak");
        usedExecCommand = true;
      }

      // execCommand("insertLineBreak") synchronously fires the `input`
      // event, which the React onInput chain below already relays as a
      // single onChange. The same DOM mutation also queues a record on
      // the MutationObserver (when isObservable). If we let the observer
      // fire, handleObserverTrigger redispatches onChange a second time
      // mid-tick and re-enters normalizeElement, which stomps the caret
      // (Asana 1214365310516577 — bottom-anchored Table2 cells visibly
      // jumped to the wrong row on Enter).
      //
      // Drain the observer's record queue here, synchronously, so its
      // microtask checkpoint sees nothing to deliver. takeRecords() is
      // race-free; a flag-based suppression cannot be (the flag-clear
      // microtask runs FIFO before the observer's callback microtask, so
      // it would already be false when the observer fires). The non-
      // execCommand branches above (splitListItem, manual LI insert) do
      // NOT fire `input`, so the observer is still their only update
      // delivery path — leave their records intact.
      if (usedExecCommand && this.observer) {
        this.observer.takeRecords();
      }

      event.stopPropagation();
      event.preventDefault();
      return;
    }
  }

  handleObserverTrigger(e) {
    if (
      e[0].type === "childList" &&
      e[0].addedNodes &&
      e[0].removedNodes &&
      e[0].addedNodes.length &&
      e[0].removedNodes.length &&
      e[0].addedNodes.length === e[0].removedNodes.length &&
      [...e[0].addedNodes].every((node, index) =>
        node.isEqualNode(e[0].removedNodes[index])
      )
    ) {
      // if the change is a childList change where removed nodes were also added then ignore it
      return;
    }
    this.handleChange({
      target: this.ref.current,
      stopPropagation: noop,
      preventDefault: noop
    });
  }

  componentDidMount() {
    this.ref.current.addEventListener(
      "keydown",
      this.replaceEnterWithLineBreak
    );

    if (this.props.isObservable) {
      this.observer = new MutationObserver(this.handleObserverTrigger);
      this.observer.observe(this.ref.current, OBSERVER_CONFIG);
    }
  }

  // Coalesce kerning preview prop changes into one rAF-deferred DOM update.
  // Per Alt+Arrow keystroke the preview slice + commit-debounce produce 2-3
  // prop transitions; without coalescing each fires a separate handler.
  scheduleKerningPreviewUpdate() {
    if (this._kerningUpdateRaf) return;
    this._kerningUpdateRaf = requestAnimationFrame(() => {
      this._kerningUpdateRaf = null;
      if (!this.props.isObservable || !this.ref.current) return;
      if (this.props.text === this.ref.current.innerHTML) return;
      // Fast path: only letter-spacing values changed (or an override hit
      // zero and was compacted out). Mutate existing spans in place — avoids
      // the expensive innerHTML parse + selection-restore cycle that blocks
      // the main thread on sustained key-repeat across structure transitions.
      if (this.applyInPlaceKerningStyleUpdate()) return;
      // Structural change beyond what the in-place path can handle (a new
      // override at a position not currently wrapped). Full rewrite.
      this.suspendObserver(() => {
        saveCurrentRange();
        this.ref.current.innerHTML = this.props.text;
        applySavedSelection();
      });
    });
  }

  // Applies a kerning prop change by mutating existing DOM spans in place.
  // Returns true on success, false to signal a structural change that
  // requires a full rewrite (target adds spans at positions not currently
  // wrapped, or wrapped chars no longer line up).
  //
  // Matches DOM and target spans by plain-text position so repeated chars in
  // the textbox can't cause a kerning value to shift onto the wrong span when
  // a neighbour clears across zero. DOM spans without a matching target
  // position have their inline letter-spacing cleared (rather than removed)
  // so the parent's global tracking inherits through; the orphan lingers
  // invisibly and gets re-styled in place if the override comes back.
  // Permanent cleanup happens on blur via the read-mode renderer.
  applyInPlaceKerningStyleUpdate() {
    const root = this.ref.current;
    if (!this.props.text || !root) return false;
    const targetSpans = parseKerningSpans(this.props.text);
    const currentSpans = collectKerningSpansWithPositions(root);
    if (targetSpans.length > currentSpans.length) return false;

    const targetByPosition = new Map();
    for (const span of targetSpans) {
      targetByPosition.set(span.position, span);
    }

    // Every target must align with an existing DOM span at the same position
    // and wrapping the same character; otherwise the structure has shifted
    // (typing, paste, etc.) and a full rewrite is required.
    for (const span of targetSpans) {
      const current = currentSpans.find(c => c.position === span.position);
      if (!current || current.el.textContent !== span.char) return false;
    }

    this.suspendObserver(() => {
      for (const current of currentSpans) {
        const target = targetByPosition.get(current.position);
        // Orphan: clear the inline value so the parent's letter-spacing
        // inherits through. "0em" would clobber a non-zero global tracking.
        const next = target ? target.value : "";
        if (current.el.style.letterSpacing !== next) {
          current.el.style.letterSpacing = next;
        }
      }
    });
    return true;
  }

  // Disconnect the MutationObserver, run fn synchronously, then re-observe.
  // Used by programmatic innerHTML rewrites (color/kerning preview updates) so
  // the rewrite doesn't loop back through handleChange → onChange → Redux.
  suspendObserver(fn) {
    const observer = this.observer;
    if (!observer) {
      fn();
      return;
    }
    observer.disconnect();
    try {
      fn();
    } finally {
      if (this.props.isObservable && this.ref.current) {
        observer.observe(this.ref.current, OBSERVER_CONFIG);
      }
    }
  }

  componentWillUnmount() {
    this.ref.current.removeEventListener(
      "keydown",
      this.replaceEnterWithLineBreak
    );
    if (this._kerningUpdateRaf) {
      cancelAnimationFrame(this._kerningUpdateRaf);
      this._kerningUpdateRaf = null;
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return true;
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.editable && this.props.editable) {
      this.ref.current.focus();
      if (this.props.selectAll) {
        const selection = window.getSelection();
        const range = document.createRange();

        range.selectNodeContents(this.ref.current);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }

    if (this.observer && !this.props.isObservable && prevProps.isObservable) {
      // isObservable is no longer true so we can remove the observer safely
      this.observer.disconnect();
      delete this.observer;
    }

    if (!this.observer && this.props.isObservable) {
      this.observer = new MutationObserver(this.handleObserverTrigger);
      this.observer.observe(this.ref.current, OBSERVER_CONFIG);
    }

    if (
      prevProps.text !== this.props.text &&
      this.props.isObservable &&
      this.props.text !== this.ref.current.innerHTML
    ) {
      if (isRichTextColorChange(prevProps.text, this.props.text)) {
        this.suspendObserver(() => {
          saveCurrentRange();
          this.ref.current.innerHTML = this.props.text;
          applySavedSelection();
        });
      } else if (isKerningOnlyChange(prevProps.text, this.props.text)) {
        this.scheduleKerningPreviewUpdate();
      }
    }
  }

  onKeyUp(event) {
    /* We stop propagation to prevent elements to be deleted from canvas */
    if (keyCodes.isBlackListed(event.keyCode)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  handlePaste(event) {
    event.preventDefault();
    event.stopPropagation();

    const data = event.nativeEvent.clipboardData.getData("text/plain");

    // get current selection range
    const { rangeStart, rangeEnd, rangeTargetIndex } = getCurrentRangeValues(
      this.ref.current
    );

    this.insertTextAtCursor(data);

    // After pasting multiline text, reapply paragraph spacing so new
    // <br><br> pairs from newlines are converted to spacer divs.
    this.props.onChange(
      stripKerningOverridesHTML(this.ref.current.innerHTML),
      event,
      value =>
        this.forceUpdateHTML(value, rangeStart, rangeEnd, rangeTargetIndex)
    );
  }

  insertTextAtCursor(text) {
    // convert the text to a html suitable format
    const insertableText = text.replace(/(?:\r\n|\r|\n)/g, "<br>");
    const selection = window.getSelection();

    // Ensure selection is valid and within this contentEditable element.
    // Browser timing issues (particularly Chrome/Windows) can cause the
    // selection to drift to the document root during paste operations.
    if (
      selection.rangeCount === 0 ||
      !this.ref.current.contains(
        selection.getRangeAt(0).commonAncestorContainer
      )
    ) {
      this.ref.current.focus();
      const recoveredRange = document.createRange();
      recoveredRange.selectNodeContents(this.ref.current);
      recoveredRange.collapse(false);
      selection.removeAllRanges();
      selection.addRange(recoveredRange);
    }

    const range = selection.getRangeAt(0);
    // clear any current data in the selection (overwrite)
    range.deleteContents();
    // build a node to insert into the textbox
    const node = document.createElement("span");
    node.innerHTML = sanitizeHTMLForTextUsingDOMNodes(insertableText);
    // insert the text
    range.insertNode(node);
    // set the cursor to the end of the inserted text
    selection.collapseToEnd();
  }

  handleTextTab(node) {
    // check if the node is a list item with a previous sibling being a list item
    const range = getRange();

    if (
      range.startOffset === range.endOffset &&
      range.startContainer.nodeName === "#text"
    ) {
      // range is closed and we are in a text node
      const anchorParent = range.startContainer.parentNode;
      if (anchorParent.nodeName === "LI") {
        if (
          anchorParent.previousSibling &&
          anchorParent.previousSibling.nodeName === "LI"
        ) {
          // the direct parent is a list item with a list item as its previous sibling
          // splitListItem(range.startContainer, anchorParent, range.startOffset);
          return nestListInPreviousSibling(range.startContainer, anchorParent);
        } else {
          // we don't want to do anything if it is a list item already but we have no previous list items
          return;
        }
      }
    }
    return indentSelection(node);
  }

  render() {
    let html = this.props.editable ? this.state.initialText : this.props.text;

    const isRenderer = PATHS.isRenderer(window.location.pathname);

    return (
      <div
        style={this.props.style}
        className={`${style.contentEditable} ${this.props.className} ${
          isRenderer ? "" : style.textCursorAnchor
        }`}
        id={`UCE-${this.props.elementId}`}
        onMouseEnter={this.props.onMouseEnter}
        onMouseLeave={this.props.onMouseLeave}
        onMouseUp={this.props.onMouseUp || noop}
        onClick={this.props.onClick}
        onKeyUp={this.onKeyUp}
        onDoubleClick={this.props.onDoubleClick}
        ref={this.ref}
        onInput={this.handleChange}
        onBlur={this.handleBlur}
        onKeyDown={this.handleKeyDown}
        contentEditable={this.props.editable}
        draggable={!this.props.editable}
        onPaste={this.handlePaste}
        dangerouslySetInnerHTML={{ __html: html }}
        data-testid="UncontrolledContentEditable"
      />
    );
  }

  /**
   * @desc takes a keydown event and applies the standard text manipulation hotkeys
   * @param {object} event - a keydown event object
   */
  applyStyleChanges(event) {
    const { disableInlineStyling, richTextRestrictions = {} } = this.props;

    const isMetaOrCtrl = event.ctrlKey || event.metaKey;
    // const isShift = event.shiftKey;

    switch (event.keyCode) {
      case keyCodes.uKey: {
        if (isMetaOrCtrl) {
          //ctrl/cmd-u
          event.preventDefault();
          if (disableInlineStyling || richTextRestrictions.underline) return;
          const node = getNearestContentEditableParentFromSelection();

          toggleUnderlineOnSelection(node);
        }
        break;
      }
      case keyCodes.iKey: {
        if (isMetaOrCtrl) {
          //ctrl/cmd-i
          event.preventDefault();
          if (disableInlineStyling || richTextRestrictions.italic) return;
          const node = getNearestContentEditableParentFromSelection();

          toggleItalicOnSelection(node);
        }
        break;
      }
      case keyCodes.bKey: {
        if (isMetaOrCtrl) {
          //ctrl/cmd-b
          event.preventDefault();
          if (disableInlineStyling || richTextRestrictions.bold) return;
          const node = getNearestContentEditableParentFromSelection();

          toggleBoldOnSelection(node);
        }
        break;
      }
      // case keyCodes.tabKey: {
      //   if (isShift) {
      //     //ctrl/cmd-tab
      //     event.preventDefault();
      //     if (disableInlineStyling || richTextRestrictions.bold) return;
      //     const node = getNearestContentEditableParentFromSelection();

      //     unIndentSelection(node);
      //     break;
      //   }
      //   //tab
      //   event.preventDefault();
      //   if (disableInlineStyling || richTextRestrictions.bold) return;
      //   const node = getNearestContentEditableParentFromSelection();

      //   this.handleTextTab(node);
      //   break;
      // }
      // case keyCodes.lKey: {
      //   if (isMetaOrCtrl) {
      //     //ctrl/cmd-l
      //     event.preventDefault();
      //     if (disableInlineStyling) return;
      //     const node = getNearestContentEditableParentFromSelection();

      //     // toggleColorOnSelection(node, color);
      //     if (window.easil.testSaveRange) {
      //       setCurrentSelectionPosition(window.easil.testSaveRange, node);
      //     }
      //   }
      //   break;
      // }
      // case keyCodes.kKey: {
      //   if (isMetaOrCtrl) {
      //     //ctrl/cmd-l
      //     event.preventDefault();
      //     if (disableInlineStyling) return;
      //     const node = getNearestContentEditableParentFromSelection();
      //   }
      //   break;
      // }
      default:
        break;
    }
  }

  handleKeyDown(event) {
    event.stopPropagation();

    /* handle any default text style modifications */
    this.applyStyleChanges(event);

    this.props.onKeyDown(event);
  }

  handleBlur(event) {
    if (this.props.shouldNotBlur) return;
    this.props.onBlur(stripKerningOverridesHTML(event.target.innerHTML), event);
  }

  forceUpdateHTML(value, rangeStart, rangeEnd, rangeTargetIndex = 0) {
    let _rangeStart = rangeStart;
    let _rangeEnd = rangeEnd;
    let _rangeTargetIndex = rangeTargetIndex;
    const target = this.ref.current;

    if (isNil(_rangeStart) || isNil(!_rangeEnd)) {
      // get current selection range
      const currentRangeValues = getCurrentRangeValues(target);
      _rangeTargetIndex = currentRangeValues.rangeTargetIndex;
      _rangeStart = currentRangeValues.rangeStart;
      _rangeEnd = currentRangeValues.rangeEnd;
    }

    // update innerHTML
    target.innerHTML = value;

    const targetChildNodes = Array.from(target.childNodes);
    let rangeTargetNode = targetChildNodes[_rangeTargetIndex];

    // compensate for if a range target is in a new node that has been stripped
    if (targetChildNodes.length - 1 < _rangeTargetIndex) {
      const filteredTargetChildNodes = targetChildNodes.filter(
        node => node.nodeName === DOM_NODE_TYPES.text
      );
      rangeTargetNode =
        filteredTargetChildNodes[filteredTargetChildNodes.length - 1];
      if (isNil(rangeTargetNode)) {
        // in the case the selection is then empty we want to escape
        return;
      }
      _rangeStart = rangeTargetNode.data.length;
      _rangeEnd = rangeTargetNode.data.length;
    }

    // set the new range on the rangeTarget child which should be a text node
    setRangeForTarget({
      target: rangeTargetNode,
      startOffset: _rangeStart,
      endOffset: _rangeEnd
    });
  }

  handleChange(event) {
    if (!event.target) {
      return;
    }
    if (!event.target?.textContent.trim().length) {
      event.target.innerHTML = "";
    }

    const { rangeStart, rangeEnd, rangeTargetIndex } = getCurrentRangeValues(
      this.ref.current
    );

    this.props.onChange(
      stripKerningOverridesHTML(event.target.innerHTML),
      event,
      value =>
        this.forceUpdateHTML(
          value,
          rangeStart - 1,
          rangeEnd - 1,
          rangeTargetIndex
        )
    );
  }
}

export default UncontrolledContentEditable;
